// "Spot the Slip" — error-correction practice items for the calm practice deck.
//
// Each item is one Portuguese sentence containing exactly ONE deliberate,
// pedagogically meaningful mistake — the kind an English-speaking A1/A2 learner
// actually makes — paired with the corrected sentence and a one-line reason.
// These map onto the Reference Shelf posters (ser/estar, por/para, gender,
// accents, gostar, pretérito/imperfeito, pronoun order, subjunctive), so the
// practice and the reference reinforce each other.
//
// The diff between `wrong` and `right` is computed at render time (word-level),
// so we only need the two full sentences plus metadata here. `tag` groups items
// for the filter chips; `slug` (optional) deep-links to the matching poster.
//
// Authored originally for Dourado. Each row:
//   [ wrong, right, tag, why, slug ]

// Per-theme expansion rows (identity … media) live in standalone
// corrections-<theme>.js modules and are appended to BASE_ROWS below.
import { EXTRA_CORRECTION_ROWS } from "./corrections-expansion.js";

const BASE_ROWS = [
  // ---- ser vs estar ---------------------------------------------------
  ["Eu sou cansado hoje.", "Eu estou cansado hoje.", "ser-estar",
    "Tiredness is a passing state, so it takes estar, not ser.", "ser-vs-estar"],
  ["A sopa é muito quente agora.", "A sopa está muito quente agora.", "ser-estar",
    "A temperature you can feel right now is a condition → estar.", "ser-vs-estar"],
  ["A minha irmã está médica.", "A minha irmã é médica.", "ser-estar",
    "A profession is part of who you are → ser.", "ser-vs-estar"],
  ["O concerto está às oito.", "O concerto é às oito.", "ser-estar",
    "An event taking place uses ser; estar would mean physical location.", "ser-vs-estar"],
  ["Onde é a casa de banho?", "Onde fica a casa de banho?", "ser-estar",
    "Location of a fixed place takes ficar (or estar), never ser.", "ser-vs-estar"],

  // ---- por vs para ----------------------------------------------------
  ["Estudo português para o meu emprego novo, por isso pratico por falar melhor.",
    "Estudo português para o meu emprego novo, por isso pratico para falar melhor.", "por-para",
    "Purpose / goal (\"in order to\") is para, not por.", "por-vs-para"],
  ["Obrigado para a tua ajuda.", "Obrigado pela tua ajuda.", "por-para",
    "Cause or motive takes por, and por + a contracts to pela.", "por-vs-para"],
  ["Este presente é por ti.", "Este presente é para ti.", "por-para",
    "A recipient takes para.", "por-vs-para"],
  ["Partimos por Lisboa amanhã.", "Partimos para Lisboa amanhã.", "por-para",
    "Destination of movement is para.", "por-vs-para"],
  ["Paguei vinte euros para o livro.", "Paguei vinte euros pelo livro.", "por-para",
    "An exchange (money for goods) is por; por + o = pelo.", "por-vs-para"],

  // ---- noun gender & agreement ----------------------------------------
  ["Tenho um problema grande com o mapa.", "Tenho um problema grande com o mapa.", "gender",
    "Trap check: o problema and o mapa are masculine despite the -a. (Already correct!)", "gender-agreement-of-nouns"],
  ["A problema é muito difícil.", "O problema é muito difícil.", "gender",
    "Greek-origin -ma words like problema are masculine → o.", "gender-agreement-of-nouns"],
  ["A minha mão direito está ferida.", "A minha mão direita está ferida.", "gender",
    "Mão is feminine despite ending in -ão, so the adjective is direita.", "gender-agreement-of-nouns"],
  ["É uma dia bonito.", "É um dia bonito.", "gender",
    "O dia is masculine; pair it with um, not uma.", "gender-agreement-of-nouns"],
  ["As casas brancos são novas.", "As casas brancas são novas.", "gender",
    "Adjectives agree in gender and number: feminine plural → brancas.", "adjective-agreement"],

  // ---- gostar & similar ----------------------------------------------
  ["Eu gosto o café.", "Eu gosto do café.", "gustar",
    "Gostar always takes the preposition de; de + o = do.", "gostar-and-similar-verbs"],
  ["Gosto muito de os livros de história.", "Gosto muito dos livros de história.", "gustar",
    "Before the thing you like, de + os must contract to dos.", "gostar-and-similar-verbs"],
  ["Gosto de correr e nadar, mas não gosto dançar.",
    "Gosto de correr e nadar, mas não gosto de dançar.", "gustar",
    "Gostar needs de even before an infinitive → gosto de dançar.", "gostar-and-similar-verbs"],
  ["Tu gostas muito o chocolate, não é?", "Tu gostas muito do chocolate, não é?", "gustar",
    "Don't drop de after gostar; de + o = do.", "gostar-and-similar-verbs"],

  // ---- pretérito vs imperfeito ---------------------------------------
  ["Quando era pequeno, fui à praia todos os verões.",
    "Quando era pequeno, ia à praia todos os verões.", "preterito-imperfecto",
    "A repeated habit in the past is the imperfeito → ia.", "a2-preterito-vs-imperfeito"],
  ["Ontem almoçava com a família e depois fui ao cinema.",
    "Ontem almocei com a família e depois fui ao cinema.", "preterito-imperfecto",
    "A single, finished event takes the pretérito perfeito → almocei.", "a2-preterito-vs-imperfeito"],
  ["Eram as três quando o telefone tocava.",
    "Eram as três quando o telefone tocou.", "preterito-imperfecto",
    "Background time stays imperfeito (eram); the interrupting event is pretérito perfeito (tocou).", "a2-preterito-vs-imperfeito"],

  // ---- clitic-pronoun placement --------------------------------------
  ["O livro? Dei-lhe-o esta manhã.", "O livro? Dei-lho esta manhã.", "pronoun-order",
    "lhe + o contracts to lho (you can't say \"lhe-o\").", "object-pronouns"],
  ["Me traz um café, por favor.", "Traz-me um café, por favor.", "pronoun-order",
    "In an affirmative command the clitic attaches to the end: traz-me.", "object-pronouns"],
  ["Não percebo-o bem.", "Não o percebo bem.", "pronoun-order",
    "After negation the clitic moves before the verb: não o percebo.", "object-pronouns"],

  // ---- present subjunctive (conjuntivo) ------------------------------
  ["Quero que vens à festa.", "Quero que venhas à festa.", "subjunctive",
    "A wish about someone else (two subjects + que) triggers the conjuntivo → venhas.", "b1-present-subjunctive"],
  ["É importante que estudas todos os dias.", "É importante que estudes todos os dias.", "subjunctive",
    "Impersonal expressions of importance take the conjuntivo → estudes.", "b1-present-subjunctive"],
  ["Espero que tens uma boa viagem.", "Espero que tenhas uma boa viagem.", "subjunctive",
    "Esperar que (hope/wish) flips the verb to the conjuntivo → tenhas.", "b1-present-subjunctive"],

  // ---- written accents ------------------------------------------------
  ["O meu medico vive numa cidade pequena.", "O meu médico vive numa cidade pequena.", "accents",
    "Médico is proparoxytone — antepenult stress always carries a written accent.", "stress-and-written-accents"],
  ["O cafe está frio esta manhã.", "O café está frio esta manhã.", "accents",
    "Café is oxytone ending in a vowel, so it needs the acute accent.", "stress-and-written-accents"],

  // ---- common A1 mix-ups ---------------------------------------------
  ["Tenho vinte anos velho.", "Tenho vinte anos.", "basics",
    "Age uses ter + anos with no word for \"old\": tenho vinte anos.", ""],
  ["A minha mãe tem calor e eu tenho vinte e oito.",
    "A minha mãe tem calor e eu tenho vinte e oito anos.", "basics",
    "Keep anos when stating age, even right after a number.", ""],
  ["Vivo aqui há dois anos e sou de Noruega.",
    "Vivo aqui há dois anos e sou da Noruega.", "basics",
    "Most country names take an article: de + a Noruega → da Noruega.", ""],
];

// Stable id + structured shape for the view.
const ROWS = [...BASE_ROWS, ...EXTRA_CORRECTION_ROWS];
export const CORRECTIONS = ROWS.map(([wrong, right, tag, why, slug], i) => ({
  id: `corr-${i + 1}`,
  wrong,
  right,
  tag,
  why,
  slug: slug || "",
}));

// Tag registry — drives the filter chips. Order is intentional (mirrors the
// Reference Shelf), label is human-readable.
export const CORRECTION_TAGS = [
  { id: "ser-estar", label: "Ser vs Estar" },
  { id: "por-para", label: "Por vs Para" },
  { id: "gender", label: "Gender & Agreement" },
  { id: "gustar", label: "Gostar" },
  { id: "preterito-imperfecto", label: "Pretérito vs Imperfeito" },
  { id: "pronoun-order", label: "Pronoun Order" },
  { id: "subjunctive", label: "Subjunctive" },
  { id: "accents", label: "Accents" },
  { id: "basics", label: "Everyday Basics" },
];
